import { APP_DOMAIN } from "@/src/utils/constants";
import React from "react";

// Styles
import styles from "@scss/custom-components/custom-iframe.module.scss";

export type CustomIframeProps = React.ComponentPropsWithoutRef<"iframe"> & {
  url: string;
  title?: string;
  width?: string | number;
  height?: string | number;
  /**
   * Intended scale factor for iframe content.
   * If given, this will scale the iframe content by the given value.
   */
  scale?: number;
  /**
   * Whether to use a fixed 16:9 aspect ratio container (`true`)
   * or flexible sizing with optional scaling (`false`).
   */
  maintainAspectRatio?: boolean;
  contentWidth?: number; // Expected width of iframe content (e.g., 1920)
  contentHeight?: number; // Expected height of iframe content (e.g., 1080)
};

// Move environment detection outside component - runs once at module load
const IS_DEV = import.meta.env.DEV;

export const CustomIframe = React.forwardRef<
  HTMLIFrameElement,
  CustomIframeProps
>(
  (
    {
      url,
      title = "Iframe",
      width = "100%",
      height = "100%",
      scale = 1,
      maintainAspectRatio = true,
      contentWidth,
      contentHeight,
      ...props
    },
    ref,
  ) => {
    const containerRef = React.useRef<HTMLDivElement>(null);

    // Calculate initial scale deterministically for known dimensions
    const initialScale = React.useMemo(() => {
      if (contentWidth === 1920 && contentHeight === 1080) {
        // Magic number: 420px container width ÷ 1920px content width = 0.21875 (optimized for 16:9 aspect ratio scaling)
        return 0.21875;
      }
      return scale;
    }, [contentWidth, contentHeight, scale]);

    const [scaleValue, setScaleValue] = React.useState(initialScale);

    // Only set up dynamic scaling for non-standard dimensions in flexible mode
    React.useEffect(() => {
      // Skip dynamic scaling if:
      // - maintainAspectRatio is true (doesn't use containerRef)
      // - no content dimensions provided
      // - 1920x1080 content (uses fixed scale)
      // - containerRef not available
      if (
        maintainAspectRatio ||
        !contentWidth ||
        !contentHeight ||
        !containerRef.current ||
        (contentWidth === 1920 && contentHeight === 1080)
      ) {
        return;
      }

      const updateScale = () => {
        if (!containerRef.current) return;

        const containerWidth = containerRef.current.offsetWidth;
        const containerHeight = containerRef.current.offsetHeight;

        const scaleX = containerWidth / contentWidth;
        const scaleY = containerHeight / contentHeight;
        const newScale = Math.min(scaleX, scaleY);

        setScaleValue(newScale);
      };

      updateScale();

      const resizeObserver = new ResizeObserver(updateScale);
      resizeObserver.observe(containerRef.current);

      return () => resizeObserver.disconnect();
    }, [contentWidth, contentHeight, maintainAspectRatio]);

    // Only calculate trust in production, skip in dev for performance
    const sandboxValue = React.useMemo(() => {
      // Allow manual override
      if (props.sandbox) {
        return props.sandbox;
      }

      // In development, trust localhost implicitly
      if (IS_DEV) {
        return "allow-scripts allow-same-origin allow-forms allow-popups allow-modals";
      }

      // In production, only trust visual app domain
      try {
        const iframeUrl = new URL(url);
        const isVisualApp = iframeUrl.hostname.endsWith(APP_DOMAIN);

        return isVisualApp
          ? "allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
          : "allow-scripts"; // Restrictive for unknown sources
      } catch {
        // Invalid URL
        return "allow-scripts";
      }
    }, [url, props.sandbox]);

    // Use aspect ratio container or flexible sizing
    if (maintainAspectRatio) {
      return (
        <div
          className={styles.customIframeMaintainAspectRatioOuter}
          style={{
            width: width,
          }}
        >
          <iframe
            ref={ref}
            src={url}
            title={title}
            sandbox={sandboxValue}
            referrerPolicy="strict-origin-when-cross-origin"
            {...props}
            className={styles.customIframeMaintainAspectRatio}
          />
        </div>
      );
    }

    // Flexible sizing - fills container with optional scaling
    return (
      <div
        ref={containerRef}
        className={styles.customIframeFlexibleAspectRatioOuter}
        style={{
          width: width,
          height: height,
        }}
      >
        <iframe
          ref={ref}
          src={url}
          title={title}
          sandbox={sandboxValue}
          referrerPolicy="strict-origin-when-cross-origin"
          className={styles.customIframeFlexibleAspectRatio}
          {...props}
          style={{
            width: contentWidth ? `${contentWidth}px` : "100%",
            height: contentHeight ? `${contentHeight}px` : "100%",
            transform:
              contentWidth && contentHeight ? `scale(${scaleValue})` : "none",
          }}
        />
      </div>
    );
  },
);

CustomIframe.displayName = "CustomIframe";
